import { useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  FullTrainingFragment,
  FundaeFinishGroupUpdateCodigoCursoDocument,
  FundaeFinishGroupUpdateCodigoCursoMutation,
  FundaeFinishGroupUpdateCodigoGrupoDocument,
  FundaeFinishGroupUpdateCodigoGrupoMutation,
  FundaeFinishGroupUpdateCostsDocument,
  FundaeFinishGroupUpdateCostsMutation,
  TrainingDetailPageDocument,
} from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import MoneyField from 'modules/core/components/MoneyField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

import { useDebounced } from 'modules/trainings/components/Revamp/TrainingsDetail/Fundae/finalizar-grupo-formativo/content/utils'
import { SalaryCostCalculator } from 'modules/trainings/components/SalaryCostCalculator'
import { validateCodigo } from 'modules/trainings/lib/fundaeUtils'
import type { TrainingsTrainingClass } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

export function Fields({
  training,
  trainingClass,
  validate,
}: {
  trainingClass: TrainingsTrainingClass
  training: FullTrainingFragment
  validate: boolean
}) {
  const { company } = useCurrentSession()
  const [fields, setFields] = useState({
    codigoCurso: training.codigoCurso || '',
    codigoGrupo: trainingClass?.fundaeConfig?.codigoGrupo || '',
    directCost: trainingClass?.cost || '',
    indirectCost: trainingClass?.indirectCost || '',
    salaryCost: trainingClass?.salaryCost || '',
  })
  const [updateCodigoCurso] = useMutation(FundaeFinishGroupUpdateCodigoCursoDocument, {
    extractorFn(data: FundaeFinishGroupUpdateCodigoCursoMutation) {
      return data.trainingsMutations.upsertSubsidyTrainingsTraining
    },
  })
  const [upsertCodigoGrupo] = useMutation(FundaeFinishGroupUpdateCodigoGrupoDocument, {
    extractorFn(data: FundaeFinishGroupUpdateCodigoGrupoMutation) {
      return data.trainingsMutations.upsertTrainingsTrainingClassFundaeConfiguration
    },
  })
  const [updateCosts] = useMutation(FundaeFinishGroupUpdateCostsDocument, {
    extractorFn(data: FundaeFinishGroupUpdateCostsMutation) {
      return data.trainingsMutations.updateTrainingsTrainingClass
    },
  })
  const debouncedUpdateCodigoCurso = useDebounced(updateCodigoCurso)
  const debouncedUpdateCosts = useDebounced(updateCosts)
  const debouncedUpsertCodigoGrupo = useDebounced(upsertCodigoGrupo)

  useEffect(() => {
    setFields({
      ...fields,
      codigoGrupo: trainingClass?.fundaeConfig?.codigoGrupo || '',
      directCost: trainingClass?.cost || '',
      indirectCost: trainingClass?.indirectCost || '',
      salaryCost: trainingClass?.salaryCost || '',
    })
  }, [trainingClass.id])

  return (
    <>
      <Box gap="s8">
        <Text size="300" weight="semibold" color="textPrimary">
          {i18n.t('trainings.trainings_detail.fundae.group_details.title')}
        </Text>
        <Box gap="s16" flexDirection="row">
          <TextField
            description={i18n.t(
              'trainings.trainings_detail.fundae.group_details.group_code.description'
            )}
            error={
              validate && !fields.codigoGrupo
                ? i18n.t('trainings.trainings_detail.fundae.required_field')
                : validateCodigo(fields.codigoGrupo)
                ? undefined
                : i18n.t('trainings.trainings_detail.fundae.invalid_code')
            }
            label={i18n.t('trainings.trainings_detail.fundae.group_details.group_code.label')}
            mandatory
            max={99999}
            maxLength={5}
            min={1}
            onChange={(newValue) => {
              setFields({
                ...fields,
                codigoGrupo: newValue,
              })
              if (validateCodigo(newValue))
                debouncedUpsertCodigoGrupo({
                  variables: {
                    codigoGrupo: newValue,
                    companyId: company.id,
                    trainingClassId: Number(trainingClass?.id),
                  },
                  refetchQueries: [TrainingDetailPageDocument],
                })
            }}
            placeholder="00001"
            type="number"
            value={fields.codigoGrupo}
          />
          <TextField
            description={i18n.t(
              'trainings.trainings_detail.fundae.group_details.course_code.description'
            )}
            error={
              validate && !fields.codigoCurso
                ? i18n.t('trainings.trainings_detail.fundae.required_field')
                : validateCodigo(fields.codigoCurso)
                ? undefined
                : i18n.t('trainings.trainings_detail.fundae.invalid_code')
            }
            label={i18n.t('trainings.trainings_detail.fundae.group_details.course_code.label')}
            mandatory
            max={99999}
            maxLength={5}
            min={1}
            onChange={(newValue) => {
              setFields({
                ...fields,
                codigoCurso: newValue,
              })
              if (validateCodigo(newValue))
                debouncedUpdateCodigoCurso({
                  variables: {
                    id: training.id,
                    codigoCurso: newValue,
                  },
                  refetchQueries: [TrainingDetailPageDocument],
                })
            }}
            placeholder="00001"
            type="number"
            value={fields.codigoCurso}
          />
        </Box>
      </Box>
      <Box gap="s8">
        <Text size="300" weight="semibold" color="textPrimary">
          {i18n.t('trainings.trainings_detail.fundae.costs.title')}
        </Text>
        <Box gap="s16" flexDirection="row">
          {/* We assume the currency is euros cause FUNDAE is only for Spain */}
          <MoneyField
            currency="EUR"
            error={
              validate && !fields.directCost
                ? i18n.t('trainings.trainings_detail.fundae.required_field')
                : undefined
            }
            label={i18n.t('trainings.trainings_detail.fundae.costs.direct_costs.label')}
            mandatory
            onChange={(newValue) => {
              setFields({
                ...fields,
                directCost: newValue,
              })
              debouncedUpdateCosts({
                variables: {
                  id: Number(trainingClass.id),
                  cost: newValue,
                },
                refetchQueries: [TrainingDetailPageDocument],
              })
            }}
            value={fields.directCost}
          />
          <MoneyField
            currency="EUR"
            label={i18n.t('trainings.trainings_detail.fundae.costs.indirect_costs.label')}
            mandatory
            error={
              validate && !fields.indirectCost
                ? i18n.t('trainings.trainings_detail.fundae.required_field')
                : undefined
            }
            onChange={(newValue) => {
              setFields({
                ...fields,
                indirectCost: newValue,
              })
              debouncedUpdateCosts({
                variables: {
                  id: Number(trainingClass.id),
                  indirectCost: newValue,
                },
                refetchQueries: [TrainingDetailPageDocument],
              })
            }}
            value={fields.indirectCost}
          />
          <LabeledInput
            isLegend
            label={i18n.t('trainings.trainings_detail.fundae.costs.salary_costs.label')}
          >
            <Box gap="s16" flexDirection="row" alignItems="center">
              <MoneyField
                currency="EUR"
                mandatory
                error={
                  validate && !fields.salaryCost
                    ? i18n.t('trainings.trainings_detail.fundae.required_field')
                    : undefined
                }
                onChange={(newValue) => {
                  setFields({
                    ...fields,
                    salaryCost: newValue,
                  })
                  debouncedUpdateCosts({
                    variables: {
                      id: Number(trainingClass.id),
                      salaryCost: newValue,
                    },
                    refetchQueries: [TrainingDetailPageDocument],
                  })
                }}
                value={fields.salaryCost}
              />
              <SalaryCostCalculator
                classId={Number(trainingClass?.id)}
                onApply={(newValue) => {
                  setFields({
                    ...fields,
                    salaryCost: newValue,
                  })
                  debouncedUpdateCosts({
                    variables: {
                      id: Number(trainingClass.id),
                      salaryCost: newValue,
                    },
                    refetchQueries: [TrainingDetailPageDocument],
                  })
                  return true
                }}
                trackingSelectedFrom="fundae"
              />
            </Box>
          </LabeledInput>
        </Box>
      </Box>
    </>
  )
}
